import React, { createContext, useContext, ReactNode } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSupplierPages, SupplierPage } from '@/hooks/useSupplierPages';

interface SupplierPageContextType {
  pages: SupplierPage[];
  activePage: SupplierPage | null;
  setActivePage: (pageId: string) => void;
  hasMultiplePages: boolean;
  loading: boolean;
}

const SupplierPageContext = createContext<SupplierPageContextType | undefined>(undefined);

export const useSupplierPageContext = () => {
  const context = useContext(SupplierPageContext);
  if (context === undefined) {
    throw new Error('useSupplierPageContext must be used within a SupplierPageProvider');
  }
  return context;
};

/**
 * Optional hook that returns undefined when outside the provider.
 * Used by components that may or may not be inside the provider (e.g., Navigation).
 */
export const useOptionalSupplierPageContext = () => {
  return useContext(SupplierPageContext);
};

interface SupplierPageProviderProps {
  children: ReactNode;
}

export const SupplierPageProvider: React.FC<SupplierPageProviderProps> = ({ children }) => {
  const { supplierProfile } = useAuthContext();
  const { pages, activePage, setActivePage, hasMultiplePages, loading } = useSupplierPages(supplierProfile?.id);

  const value: SupplierPageContextType = {
    pages,
    activePage,
    setActivePage,
    hasMultiplePages,
    loading,
  };

  return (
    <SupplierPageContext.Provider value={value}>
      {children}
    </SupplierPageContext.Provider>
  );
};
